import { createRouter, createWebHistory } from 'vue-router'
import MainLayout from '@/components/templates/MainLayout.vue'
import { useAuthStore } from '@/stores/auth.store'
import type { AccessLevel } from '@/types/auth.types'

// Convenience aliases (extend as you add role-gated routes)
const ALL: AccessLevel[] = ['access_level.admin', 'access_level.manager', 'access_level.user']

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Auth routes (outside MainLayout)
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/views/auth/AuthCallbackView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      component: MainLayout,
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/Dashboard.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/account',
          name: 'account',
          component: () => import('@/views/Account.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/customers',
          name: 'customer-list',
          component: () => import('@/views/customer/CustomerList.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/customers/:id',
          name: 'customer-detail',
          component: () => import('@/views/customer/CustomerDetail.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/settings/wallet-types',
          name: 'settings-wallet-types',
          component: () => import('@/views/settings/SettingsWalletTypes.vue'),
          // Editing the labels is gated server-side to AccessLevel.ADMIN
          // (it patches static_text). Client-side we keep ALL roles to avoid
          // a 404 for non-admins; the server returns 403 if they try to save.
          meta: { access_levels: ALL },
        },
        {
          path: '/settings/formats',
          name: 'settings-formats',
          component: () => import('@/views/settings/SettingsFormats.vue'),
          // Same pattern as wallet-types: view is open, edit is admin-only
          // server-side (PATCH /wallet-format/{id}). UI hides edit buttons
          // for non-admins; server enforces.
          meta: { access_levels: ALL },
        },
        // Add domain-entity routes here — see docs/guides/adding-entities.md
      ],
    },
  ],
})

// Navigation guard — redirect to login if not authenticated, check role access
let authInitialized = false

/** Called from main.ts after auth.init() completes */
export function markAuthInitialized() {
  authInitialized = true
}

router.beforeEach(async (to) => {
  if (to.meta.public) return true

  const authStore = useAuthStore()

  // On first navigation (page load), auth might not be initialized yet.
  // Wait for it — init() is called in main.ts before mount, but the
  // router may start resolving before mount completes.
  if (!authInitialized) {
    try {
      await authStore.init()
    } catch {
      // init failed — will redirect to login below
    }
    authInitialized = true
  }

  if (!authStore.isAuthenticated) {
    return { name: 'login' }
  }

  // Role-based access check
  const requiredRoles = to.meta.access_levels as AccessLevel[] | undefined
  if (requiredRoles && authStore.currentUser) {
    if (!requiredRoles.includes(authStore.currentUser.access_level)) {
      return { name: 'dashboard' }
    }
  }

  return true
})

export default router
